import {
  CreditEffectOptions,
  IUpdateSettingsRequest,
  ACCOUNTING_CONFIG,
} from "shared";

import { InputTextAddon } from "../../../../../common/Atoms/InputTextAddon";
import { OnChangeFn } from "../Main/GeneralSettings";
import { Toggle } from "../../../../../common/Atoms/Toggle";
import { Select } from "../../../../../common/Atoms/Select";
import { HelpIcon } from "../../../Molecules/HelpIcon";
import { TwoColFormRow } from "../../../Molecules/Form/TwoColFormRow";
import { CheckMobileNumbersButton, FixMobileNumbersButton } from "../../../CRM/CheckMobileNumbersButton";
import { useAccountingSystemName } from '../../../../hooks/useAccountingSystemName';
import { useAccountingSystemSetting } from '../../../../hooks/useAccountingSystemSetting';
import { useAccountingSystemTerm } from '../../../../hooks/useAccountingSystemTerm';

interface DefaultBehaviourProps {
  formData: IUpdateSettingsRequest;
  onChange: OnChangeFn;
}

const behaviourOptions = [
  {
    label: `Show & Deduct`,
    tooltip: `Show on statements and deduct from total balance`,
    value: CreditEffectOptions.Deduct,
  },
  {
    label: `Show Only`,
    tooltip: `Show on statements but do not deduct from total balance`,
    value: CreditEffectOptions.DisplayOnly,
  },
  {
    label: `Ignore`,
    tooltip: `Do not show on statements or deduct from total balance`,
    value: CreditEffectOptions.Ignore,
  },
];

interface ToggleRow {
  name: keyof IUpdateSettingsRequest;
  label: string;
  description: string;
  helpIcon: string;
}

interface SelectRow {
  name: keyof IUpdateSettingsRequest;
  label: string;
  description: string;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className={ `first:mt-0 mt-9` }>
      <div className={ `text-[11px] font-bold uppercase tracking-[.09em] text-gray-400 pb-2.5 border-b border-gray-200` }>
        { title }
      </div>
      { children }
    </div>
  );
}

export function DefaultBehaviour({ formData, onChange }: DefaultBehaviourProps) {
  const accountingSystemName = useAccountingSystemName();
  const supportsMarkAsSent = useAccountingSystemSetting(ACCOUNTING_CONFIG.SUPPORTS_MARK_AS_SENT);
  const supportsPrepayments = useAccountingSystemSetting(ACCOUNTING_CONFIG.SUPPORTS_PREPAYMENTS);
  const contactsTerm = useAccountingSystemTerm(`Contacts`);
  const quotesTerm = useAccountingSystemTerm(`Quotes`);
  const creditNotesTerm = useAccountingSystemTerm(`Credit Notes`);
  const salesDocuments = [`invoices`, quotesTerm, creditNotesTerm, ...(supportsPrepayments ? [`prepayments`, `overpayments`] : [])]
    .map(d => d.toLowerCase());

  function handleChangeBoolean({ name }) {
    onChange(name, !formData[name]);
  }

  const automationRows: ToggleRow[] = [
    ...(supportsMarkAsSent ? [{
      name: `setSendFlagLateFee` as const,
      label: `Mark late fee invoices as sent`,
      description: `When enabled, new late fee and statement interest invoices will automatically be marked as sent in ${accountingSystemName}, once the notification email has been sent`,
      helpIcon: `If the invoice is Draft, or no notification email is sent. The invoice will not be marked as sent.`,
    }] : []),
    {
      name: `autofreezeInvoice`,
      label: `Autofreeze invoices`,
      description: `When enabled, Paidnice will automatically freeze invoices that have payments reversed`,
      helpIcon: `Enabling this option will freeze invoices if they have a payment reversed. Frozen invoices will need to be manually unfrozen before invoice policies apply again.`,
    },
    {
      name: `useExpectedPaymentDate`,
      label: `Freeze until expected payment date`,
      description: `When enabled, invoices that have an expected payment date in the future will not have any policies applied to them until that date.`,
      helpIcon: `Works similar to freezing an invoice until a certain date.`,
    },
    {
      name: `promiseUpdatesExpectedPaymentDate`,
      label: `Payment promises set the expected payment date`,
      description: `When enabled, recording a payment promise also sets the invoice expected payment date (synced to your accounting system).`,
      helpIcon: `Also applies to promises submitted in the portal. In-app users can still change this per promise. Cancelling a promise does not clear the expected payment date.`,
    },
  ];

  const communicationRows: ToggleRow[] = [
    {
      name: `onlySendPrimaryContact`,
      label: `Only send to primary contact`,
      description: `When enabled, Paidnice will only send emails to the primary contact.`,
      helpIcon: `If the primary contact has no email, but other contact persons do. Nothing will be sent.`,
    },
    {
      name: `syncNotesOnAction`,
      label: `Sync notes after each action`,
      description: `When enabled, Paidnice will sync a note to your accounting system for each action taken.`,
      helpIcon: `This creates a record of actions taken by Paidnice in your accounting system on each invoice or contact.`,
    },
    {
      name: `hideNonCustomers`,
      label: `Hide contacts with no invoices or sales documents`,
      description: `Hide and ignore ${contactsTerm.toLowerCase()} that have never had any ${salesDocuments.slice(0, -1).join(`, `)} or ${salesDocuments.at(-1)}.`,
      helpIcon: `This helps keep your contact lists clean and easier to work with. Disable if you need to group a contact before they are invoiced.`,
    },
  ];

  const creditRows: SelectRow[] = [
    {
      name: `creditNoteEffect`,
      label: `Credit Note Behaviour`,
      description: `How credit notes affect outstanding balances on reminders and statements.`,
    },
    ...(supportsPrepayments ? [
      {
        name: `overpaymentsEffect` as const,
        label: `Overpayment Behaviour`,
        description: `How overpayments affect outstanding balances.`,
      },
      {
        name: `prepaymentsEffect` as const,
        label: `Prepayment Behaviour`,
        description: `How prepayments affect outstanding balances.`,
      },
    ] : []),
  ];

  const renderToggle = (row: ToggleRow) => (
    <Toggle
      key={ row.name }
      className={ `py-4 border-b border-gray-100` }
      label={ row.label }
      name={ row.name }
      description={ row.description }
      helpIcon={ row.helpIcon }
      checked={ !!formData[row.name] }
      onChange={ handleChangeBoolean }
    />
  );

  return (
    <TwoColFormRow
      heading={ `Default Behaviour` }
      description={ `Control some of the common default behaviours of Paidnice. These settings apply to all groups & policies within this organisation.` }
    >
      <Section title={ `Invoice automation` }>
        { automationRows.map(renderToggle) }

        <div className={ `flex items-center justify-between gap-5 py-4 border-b border-gray-100` }>
          <div>
            <span className={ `text-sm font-medium text-gray-500` }>{ `Payment promise grace period` }</span>
            <p className={ `mt-2 text-sm text-gray-500` }>
              { `Days after the promised date to wait for payment before the promise is marked as broken.` }
            </p>
          </div>
          <div className={ `w-32 shrink-0` }>
            <InputTextAddon
              name={ `promiseGraceDays` }
              dataType={ `number` }
              addOnText={ `days` }
              value={ formData.promiseGraceDays ?? 1 }
              onChange={ ({ value }) => onChange(`promiseGraceDays`, value === `` ? null : Number(value)) }
            />
          </div>
        </div>
      </Section>

      <Section title={ `Communication & sync` }>
        { communicationRows.map(renderToggle) }

        <div className={ `flex items-center justify-between gap-5 py-4 border-b border-gray-100` }>
          <div>
            <span className={ `text-sm font-medium text-gray-500` }>{ `Check mobile numbers` }</span>
            <p className={ `mt-2 text-sm text-gray-500` }>
              { `Checks the format of every contact's mobile number and flags the ones that SMS can't be sent to.` }
            </p>
          </div>
          <div className={ `flex shrink-0 gap-2` }>
            <CheckMobileNumbersButton />
            <FixMobileNumbersButton />
          </div>
        </div>
      </Section>

      <Section title={ `Credits & prepayments` }>
        {
          creditRows.map(row => (
            <div
              key={ row.name }
              className={ `flex items-center justify-between gap-5 py-3 border-b border-gray-100` }
            >
              <div>
                <span className={ `flex flex-row text-sm font-medium text-gray-500` }>
                  { row.label }
                  <HelpIcon
                    className={ `ml-2` }
                    tooltip={ `Does not apply to activity statements` }
                  />
                </span>
                <p className={ `mt-2 text-sm text-gray-500` }>{ row.description }</p>
              </div>
              <div className={ `w-56 shrink-0` }>
                <Select
                  name={ row.name }
                  options={ behaviourOptions }
                  selected={ formData[row.name] as string }
                  onChange={ e => onChange(row.name, e.value) }
                />
              </div>
            </div>
          ))
        }
      </Section>
    </TwoColFormRow>
  );
}
